import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';

import { AuthCard } from '@/components/AuthCard';
import { Button } from '@/components/ui/button';
import { Field, FormError } from '@/components/ui/field';
import { signIn } from '@/lib/auth-client';
import { isEmailIdentifier } from '@shared/schemas/user';

/*
 * One field for the login, accepting an email address or a username.
 *
 * The prototype's sign-in does the same, and it has to: there are accounts whose
 * login is a username because the person has no work email. Which endpoint the
 * value goes to is decided here rather than asked of the user — nobody should
 * have to know that "email or username" is two different requests.
 */

/**
 * What to tell somebody whose sign-in did not work.
 *
 * **One message for a wrong password and for an account that does not exist.**
 * Distinguishing them turns this form into a way to find out who has an account
 * here.
 *
 * A deactivated account is the exception and says so, because that person needs
 * to ring the office rather than try a different password. It is recognised by
 * the `ACCOUNT_INACTIVE` code the server sends, **not by the status** — this
 * used to read `status === 403` and told anyone hitting a
 * `trustedOrigins` mismatch that their account had been deactivated. That is a
 * configuration problem being reported as a personnel one, and it was found by
 * the first Playwright run, which browsed a port `APP_ORIGIN` did not name.
 */
function messageFor(error: { status?: number; code?: string }): string {
  if (error.code === 'ACCOUNT_INACTIVE') {
    return 'This account is no longer active. Contact the office.';
  }
  if (error.status === 429) {
    return 'Too many attempts. Wait a few minutes and try again.';
  }
  if (error.status === 401) {
    return 'That login and password do not match.';
  }

  // Anything else is ours, not theirs. Saying so is better than blaming their
  // password for a server that is misconfigured or down.
  return 'Something went wrong signing you in. Try again — if it keeps happening, ring the office.';
}

export function LoginPage() {
  const navigate = useNavigate();

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(undefined);
    setSubmitting(true);

    const trimmed = identifier.trim();

    const result = isEmailIdentifier(trimmed)
      ? await signIn.email({ email: trimmed, password, rememberMe })
      : await signIn.username({ username: trimmed, password, rememberMe });

    setSubmitting(false);

    if (result.error) {
      setError(messageFor(result.error));
      return;
    }

    // /launch reads the role and sends them on. Going straight to /admin here
    // would mean this page deciding what every role is allowed to see.
    await navigate('/launch', { replace: true });
  }

  return (
    <AuthCard
      title="Sign in"
      description="Use your email address or your username."
      footer={
        <Link
          className="text-body text-bark-700 underline underline-offset-4"
          to="/forgot-password"
        >
          Forgotten your password?
        </Link>
      }
    >
      <form onSubmit={(event) => void onSubmit(event)} className="space-y-5" noValidate>
        <FormError>{error}</FormError>

        <Field
          label="Email or username"
          name="username"
          // `username` webauthn hint covers both — a password manager offers to
          // fill either against this one field.
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          autoFocus
          required
          value={identifier}
          onChange={(event) => {
            setIdentifier(event.target.value);
          }}
        />

        <Field
          label="Password"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
          }}
        />

        <label className="flex min-h-touch cursor-pointer items-center gap-2.5 text-body text-ink-700">
          <input
            type="checkbox"
            name="rememberMe"
            checked={rememberMe}
            onChange={(event) => {
              setRememberMe(event.target.checked);
            }}
            className="size-[18px] min-h-0 accent-honey-500"
          />
          Keep me signed in on this device
        </label>

        <Button type="submit" full size="lg" disabled={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </AuthCard>
  );
}
